import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Mail, Globe, Smartphone, Calendar, Dumbbell } from 'lucide-react';

const TABS = [
  { id: 'email', label: 'E-Mail', icon: Mail },
  { id: 'portal', label: 'Portal', icon: Globe },
  { id: 'app', label: 'App', icon: Smartphone },
];

const readableText = (hex) => {
  try {
    const h = (hex || '').replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const toLin = (v) => {
      const s = parseInt(v, 16) / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    // WCAG relative Luminanz + Kontrast gegen Schwarz bzw. Weiss vergleichen.
    const L = 0.2126 * toLin(full.slice(0, 2)) + 0.7152 * toLin(full.slice(2, 4)) + 0.0722 * toLin(full.slice(4, 6));
    const contrastWhite = 1.05 / (L + 0.05);
    const contrastBlack = (L + 0.05) / 0.05;
    return contrastBlack >= contrastWhite ? '#0f172a' : '#ffffff';
  } catch {
    return '#ffffff';
  }
};

// Theme je E-Mail-Layout (passend zu email_service.py _THEMES).
const EMAIL_THEMES = {
  bold: { page: '#0a0a0a', card: '#0a0a0a', text: '#ffffff', muted: '#9ca3af', border: '#1f1f1f', dark: true },
  standard: { page: '#f3f4f6', card: '#ffffff', text: '#18181b', muted: '#6b7280', border: '#e5e7eb', dark: false },
  modern: { page: '#eef2f7', card: '#ffffff', text: '#111827', muted: '#6b7280', border: '#e5e7eb', dark: false },
  elegant: { page: '#faf9f7', card: '#ffffff', text: '#1f2937', muted: '#9ca3af', border: '#ece9e4', dark: false },
  minimal: { page: '#ffffff', card: '#ffffff', text: '#111827', muted: '#9ca3af', border: '#f1f1f1', dark: false },
};

const LogoBadge = ({ logoUrl, accent, size = 40 }) => (
  <div
    className="rounded-lg flex items-center justify-center overflow-hidden shrink-0"
    style={{ width: size, height: size, backgroundColor: logoUrl ? '#ffffff' : accent, border: logoUrl ? '1px solid #e5e7eb' : 'none' }}
  >
    {logoUrl ? (
      <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
    ) : (
      <Dumbbell className="h-5 w-5" style={{ color: readableText(accent) }} />
    )}
  </div>
);

const BrandPreview = ({ settings = {} }) => {
  const [active, setActive] = useState('email');
  // Farben JE KANAL getrennt (User-Wunsch 07/2026, ohne gemeinsame Hauptfarbe).
  const portalAccent = settings.primary_color || '#22c55e';
  const emailAccent = settings.email_accent_color || settings.primary_color || '#22c55e';
  const appAccent = settings.app_accent_color || settings.primary_color || '#22c55e';
  const name = settings.studio_name || 'Ihr Betrieb';
  const subtitle = settings.studio_subtitle || 'Kundenportal';
  const logoUrl = settings.logo_url || '';

  const activeAccent = active === 'email' ? emailAccent : active === 'app' ? appAccent : portalAccent;
  const onActiveText = readableText(activeAccent);

  const layout = (settings.email_layout || 'bold').toLowerCase();
  const th = EMAIL_THEMES[layout] || EMAIL_THEMES.bold;
  const emailOnAccent = readableText(emailAccent);

  return (
    <div data-testid="brand-preview">
      <Label className="mb-2 block">Marken-Vorschau (Live)</Label>
      <p className="text-xs text-gray-500 mb-3">Jeder Kanal nutzt seine eigene Farbe. Änderungen erscheinen sofort – auch vor dem Speichern.</p>

      {/* Channel switch */}
      <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50 mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.id;
          const tabAccent = t.id === 'email' ? emailAccent : t.id === 'app' ? appAccent : portalAccent;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t.id)}
              data-testid={`brand-preview-tab-${t.id}`}
              className="px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors"
              style={isActive ? { backgroundColor: tabAccent, color: readableText(tabAccent) } : { color: '#64748b' }}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* EMAIL — spiegelt gewähltes Layout + E-Mail-Farbe */}
      {active === 'email' && (
        <div
          className="max-w-md rounded-xl border overflow-hidden shadow-sm"
          data-testid="brand-preview-email"
          style={{ backgroundColor: th.page, borderColor: th.border }}
        >
          <div className="p-4">
            <div className="rounded-lg overflow-hidden" style={{ backgroundColor: th.card, border: `1px solid ${th.border}` }}>
              <div className="px-5 py-4 flex items-center gap-3" style={{ backgroundColor: emailAccent }}>
                <LogoBadge logoUrl={logoUrl} accent={emailAccent} />
                <span className="font-bold text-base" style={{ color: emailOnAccent }}>{name}</span>
              </div>
              <div className="p-5 space-y-3">
                <p className="font-semibold" style={{ color: th.text }}>Hallo Max,</p>
                <p className="text-sm" style={{ color: th.muted }}>deine Buchung für morgen um 10:00 Uhr ist bestätigt. Wir freuen uns auf dich!</p>
                <button className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ backgroundColor: emailAccent, color: emailOnAccent }}>
                  Termin ansehen
                </button>
              </div>
              <div className="px-5 py-3 text-[11px] border-t" style={{ color: th.muted, borderColor: th.border }}>
                {name} · Diese E-Mail wurde automatisch versendet.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PORTAL */}
      {active === 'portal' && (
        <div className="max-w-md rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-testid="brand-preview-portal">
          <div className="h-7 bg-gray-100 border-b border-gray-200 flex items-center px-3 gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <div
            className="p-5"
            style={{ background: `linear-gradient(135deg, ${settings.background_gradient_from || '#f8fafc'} 0%, ${settings.background_gradient_to || '#eef2f7'} 100%)` }}
          >
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <LogoBadge logoUrl={logoUrl} accent={portalAccent} />
                <div>
                  <p className="font-bold text-gray-900">{name}</p>
                  <p className="text-xs text-gray-500">{subtitle}</p>
                </div>
              </div>
              <button className="w-full py-2 rounded-lg text-sm font-semibold" style={{ backgroundColor: portalAccent, color: readableText(portalAccent) }}>
                Termin buchen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* APP */}
      {active === 'app' && (
        <div className="flex justify-start" data-testid="brand-preview-app">
          <div className="w-[230px] rounded-[2rem] border-[6px] border-gray-900 overflow-hidden shadow-xl bg-white">
            <div className="h-5 bg-gray-900 flex justify-center items-end pb-0.5">
              <span className="w-16 h-1.5 rounded-full bg-gray-700" />
            </div>
            <div className="px-4 py-5 flex flex-col items-center text-center" style={{ backgroundColor: appAccent }}>
              <LogoBadge logoUrl={logoUrl} accent={appAccent} size={56} />
              <p className="mt-2 font-bold" style={{ color: readableText(appAccent) }}>{name}</p>
              <p className="text-[11px]" style={{ color: readableText(appAccent), opacity: 0.85 }}>{settings.app_slogan || 'Dein Personal Trainer'}</p>
            </div>
            <div className="p-3 space-y-2">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                <Calendar size={16} style={{ color: appAccent }} />
                <span className="text-xs text-gray-700">Nächster Termin</span>
              </div>
              <button className="w-full py-2 rounded-lg text-xs font-semibold" style={{ backgroundColor: appAccent, color: readableText(appAccent) }}>
                Jetzt buchen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandPreview;
